import type { Student } from "../providers/studentsProvider";
import type { FilterMode } from "../backoffice.utils";

const SIGNED_DOCUMENT_STATES = [
  "signedByInstructorAndStudent",
  "completed", // legacy value
];

/**
 * Filters `students` based on the state of their Ausbildungsnachweis signature requests.
 *
 * The `ausbildungsnachweisState` array must contain either `"signed"`, `"notSigned"`, or both:
 * - `["signed"]`: only students whose Ausbildungsnachweis documents are all signed.
 * - `["notSigned"]`: only students that have at least one unsigned Ausbildungsnachweis document.
 * - `["signed", "notSigned"]`: all students that have any Ausbildungsnachweis document, regardless of its state.
 */
export function filterByAusbildungsnachweisState(
  students: Student[],
  signatureRequests: Array<{ studentUid: string; state: string }>,
  ausbildungsnachweisState: Array<"signed" | "notSigned">,
  mode: FilterMode,
): Student[] {
  // Build a map of students with ausbildungsnachweis documents and their signed state.
  const studentsWithAusbildungsnachweisMap = new Map<string, boolean>();
  for (const signatureRequest of signatureRequests) {
    if (!studentsWithAusbildungsnachweisMap.has(signatureRequest.studentUid)) {
      studentsWithAusbildungsnachweisMap.set(
        signatureRequest.studentUid,
        SIGNED_DOCUMENT_STATES.includes(signatureRequest.state),
      );
    } else if (
      // If the student has at least 1 notSigned ausbildungsnachweis document, he's considered as notSigned, even if he has other signed ausbildungsnachweis documents.
      studentsWithAusbildungsnachweisMap.get(signatureRequest.studentUid) &&
      !SIGNED_DOCUMENT_STATES.includes(signatureRequest.state)
    ) {
      studentsWithAusbildungsnachweisMap.set(signatureRequest.studentUid, false);
    }
  }
  // Filter students based on the map of students with ausbildungsnachweis documents and their signed state.
  return students.filter((s) => {
    const matches =
      ausbildungsnachweisState.length === 2
        ? studentsWithAusbildungsnachweisMap.has(s.id) // Include all students with ausbildungsnachweis documents, regardless of their signed state
        : studentsWithAusbildungsnachweisMap.get(s.id) === (ausbildungsnachweisState[0] === "signed"); // Include only students with ausbildungsnachweis documents that match the signed/notSigned state specified in the filter
    return mode === "included" ? matches : !matches;
  });
}
